import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { firebaseConfig } from "config/firebase";
import {
  CHANGE_PASSWORD_MUTATION,
  CONFIRM_FORGET_PASSWORD_MUTATION,
  REQUEST_FORGET_PASSWORD_MUTATION,
} from "graphql/auth/mutation";
import { useFirebase } from "hooks/firebase/firebase";
import { useUpdateUserNotification } from "hooks/user/user";
// import { authZitadelManager } from "config/auth";
import { TOKEN } from "config/constants";
import { GET_USER_CURRENT } from "graphql/auth/query";
import { useCookies } from "react-cookie";
import { useHistory } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useEffect, useState, useMemo, useCallback } from "react";
import { createZitadelAuth } from "@zitadel/react";

// export const storageKey = `oidc.user:${process.env.REACT_APP_OAUTH2_SERVER_URL}:${process.env.REACT_APP_OAUTH2_CLIENT_ID}`;

export const useAuthToken = () => {
  // get normal token
  const [cookies, setCookie, removeCookie] = useCookies([TOKEN]);
  const tokenNormal = cookies[TOKEN];
  const setAuthNormalToken = (authToken) => setCookie(TOKEN, authToken, { path: "/" });
  const removeAuthNormalToken = () => {
    removeCookie(TOKEN, { path: "/" });
    document.cookie = `${TOKEN}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  // get zitadel token
  const { idToken: zitadelToken } = useAuth();
  const isTokenLoginZitadel = Boolean(zitadelToken);

  return {
    token: zitadelToken || tokenNormal,
    isTokenLoginZitadel,
    setAuthNormalToken,
    removeAuthNormalToken,
  };
};

export const useLogout = () => {
  const apolloClient = useApolloClient();
  const { logoutWithoutApollo } = useLogoutWithoutApollo();
  const { getMessagingToken } = useFirebase(firebaseConfig);
  const { handleUpdateUserNotificationToken } = useUpdateUserNotification();

  const logout = async () => {
    // remove notification token if exist
    const token = await getMessagingToken();
    if (token) {
      try {
        await handleUpdateUserNotificationToken(token, true);
      } catch (e) {
        console.error(e);
      }
    }

    await logoutWithoutApollo();
    await apolloClient.clearStore();
  };

  return {
    logout,
    logoutWithoutApollo,
  };
};

export const useLogoutWithoutApollo = () => {
  const { removeAuthNormalToken, isTokenLoginZitadel } = useAuthToken();
  const { logout: logoutZitadel } = useAuth();
  const history = useHistory();

  const logoutWithoutApollo = async () => {
    if (isTokenLoginZitadel) {
      logoutZitadel();
    } else {
      history?.push({
        pathname: "/login",
      });
      removeAuthNormalToken();
    }
  };

  return {
    logoutWithoutApollo,
  };
};

export const useChangePassword = () => {
  const [mutate, { loading }] = useMutation(CHANGE_PASSWORD_MUTATION);
  const handleChangePassword = async (params) => {
    await mutate({
      variables: params,
    });
  };

  return {
    loading,
    handleChangePassword,
  };
};

export const useRequestForgetPassword = () => {
  const [mutate, { loading }] = useMutation(REQUEST_FORGET_PASSWORD_MUTATION);
  const handleRequestForgetPassword = async (params) => {
    await mutate({
      variables: params,
    });
  };

  return {
    loading,
    handleRequestForgetPassword,
  };
};

export function useConfirmForgetPassword() {
  const [mutate, { loading }] = useMutation(CONFIRM_FORGET_PASSWORD_MUTATION);

  const handleConfirmForgetPassword = async (params) => {
    await mutate({
      variables: params,
    });
  };
  return {
    loading,
    handleConfirmForgetPassword,
  };
}

export const useGetUserCurrent = () => {
  const { data } = useQuery(GET_USER_CURRENT);

  return {
    userCurrentData: data?.user?.me,
  };
};

/**
 * Hook quản lý đăng nhập, đăng xuất và token của người dùng với Zitadel
 */
export function useZitadelAuth(config) {
  const zitadel = useMemo(() => createZitadelAuth(config), [config]);

  const [authenticated, setAuthenticated] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = useCallback(() => {
    zitadel.authorize();
  }, [zitadel]);

  const logout = useCallback(() => {
    zitadel.signout();
  }, [zitadel]);

  /**
   * Kiểm tra token có hết hạn không
   */
  const isTokenExpired = useCallback((user) => {
    if (!user || !user.expires_at) return true;

    const expiresAt = user.expires_at * 1000; // convert seconds → ms
    const now = Date.now();
    const bufferTime = 60 * 1000; // 60s buffer

    return now >= expiresAt - bufferTime;
  }, []);

  /**
   * Hàm load user lần đầu khi khởi tạo app
   */
  const loadUser = useCallback(async () => {
    setLoading(true);
    try {
      let user = await zitadel.userManager.getUser();

      if (!user) {
        // eslint-disable-next-line no-restricted-syntax
        console.log("[Zitadel] No user found in storage");
        setAuthenticated(false);
        setAccessToken(null);
        setIdToken(null);
        return;
      }

      // Nếu token hết hạn → cố gắng làm mới qua silent signin
      if (isTokenExpired(user)) {
        // eslint-disable-next-line no-restricted-syntax
        console.log("[Zitadel] Token expired, attempting silent signin...");
        try {
          user = await zitadel.userManager.signinSilent();
          // eslint-disable-next-line no-restricted-syntax
          console.log("[Zitadel] Silent signin successful");
        } catch (silentError) {
          console.warn("[Zitadel] Silent signin failed:", silentError);
          await zitadel.userManager.removeUser(); // xoá cache cũ nếu silent thất bại
          await zitadel.signout();
          setAuthenticated(false);
          setAccessToken(null);
          setIdToken(null);
          return;
        }
      }

      // if (user) {
      // Sau khi silent signin (hoặc nếu token vẫn hợp lệ)
      setAuthenticated(true);
      setAccessToken(user.access_token || null);
      setIdToken(user.id_token || null);
      // }
    } catch (error) {
      console.error("[Zitadel] Load user error:", error);
      setAuthenticated(false);
      setAccessToken(null);
      setIdToken(null);
    } finally {
      setLoading(false);
    }
  }, [zitadel, isTokenExpired]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  /**
   * Lắng nghe event của userManager để cập nhật token realtime
   */
  useEffect(() => {
    const onUserLoaded = (user) => {
      // eslint-disable-next-line no-restricted-syntax
      console.log("[Zitadel] User loaded, updating tokens");
      setAccessToken(user.access_token);
      setIdToken(user.id_token);
      setAuthenticated(true);
    };

    const onUserUnloaded = () => {
      // eslint-disable-next-line no-restricted-syntax
      console.log("[Zitadel] User unloaded");
      setAuthenticated(false);
      setAccessToken(null);
      setIdToken(null);
    };

    zitadel.userManager.events.addUserLoaded(onUserLoaded);
    zitadel.userManager.events.addUserUnloaded(onUserUnloaded);

    return () => {
      zitadel.userManager.events.removeUserLoaded(onUserLoaded);
      zitadel.userManager.events.removeUserUnloaded(onUserUnloaded);
    };
  }, [zitadel]);

  return {
    authenticated,
    accessToken,
    idToken,
    loading,
    login,
    logout,
    loadUser,
    userManager: zitadel.userManager,
  };
}
